import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode, Ref } from 'react';

import { SCROLL_FADE_HEADER_PROPS } from '../scroll-fade/ScrollFade';

type TileProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
    readonly ref?: Ref<HTMLDivElement>;
};

export const Tile: FunctionComponent<TileProps> = ({ children, className, ...props }) => (
    <div
        data-testid="tile"
        className={clsx(
            'flex flex-col items-start justify-start font-sans',
            'bg-surface-raised border border-border-subtle rounded-xxxl text-text-primary',
            'gap-xxl h-full w-full box-border',
            'overflow-hidden',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

type TileContentProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

type TileHeaderProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

type TileFiltersProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

export const TileTitle: FunctionComponent<HTMLAttributes<HTMLHeadingElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <h2 className={clsx('flex text-text-content-faint text-body-m font-normal m-0 text-xxs', className)} {...props}>
        {children}
    </h2>
);

export const TileHeader: FunctionComponent<TileHeaderProps> = ({ children, className, ...props }) => (
    <div className={clsx('flex w-full min-w-0 shrink-0 items-center gap-m font-sans', className)} {...props}>
        {children}
    </div>
);

export const TileFilters: FunctionComponent<TileFiltersProps> = ({ children, className, ...props }) => (
    <div
        className={clsx('flex min-w-0 flex-1 basis-0 items-center justify-end gap-s whitespace-nowrap', className)}
        {...props}
    >
        {children}
    </div>
);

export const TileContent: FunctionComponent<TileContentProps> = ({ children, className, ...props }) => (
    <div
        className={clsx(
            'flex flex-col items-start justify-start',
            'gap-xxl h-full box-border',
            'pb-xxl px-6xl',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

type TileControlGroupProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

/** Related tile controls (close, expand) share one pill; each stays its own button. */
export const TileControlGroup: FunctionComponent<TileControlGroupProps> = ({ children, className, ...props }) => (
    <div
        className={clsx(
            'inline-flex shrink-0 items-center rounded-full p-px',
            'bg-action-secondary shadow-action-secondary',
            '[&>button]:!bg-transparent [&>button]:!shadow-none',
            '[&>button:not(:disabled):hover]:!bg-surface-interactive',
            className,
        )}
        {...props}
    >
        {children}
    </div>
);

/**
 * For a tile's control row inside `TileContent`: spans the content width, so the controls keep
 * the content padding and line up with the content's edges.
 */
export const tileContentEdgeOffsetClassNames = 'w-auto self-stretch';

type TileStickyHeaderProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
    /** Tighter gap to the content below, for headers followed by breadcrumbs or a title. */
    readonly compact?: boolean;
};

/** A tile's sticky control row inside `TileContent`, with its controls aligned to the content edges. */
export const TileStickyHeader: FunctionComponent<TileStickyHeaderProps> = ({
    children,
    className,
    compact = false,
    ...props
}) => (
    <div
        className={clsx(
            'sticky top-0 z-[1] bg-surface-raised pt-tile-edge',
            compact ? 'pb-m' : 'pb-xxl',
            tileContentEdgeOffsetClassNames,
            className,
        )}
        {...props}
        {...SCROLL_FADE_HEADER_PROPS}
    >
        {children}
    </div>
);
